/* Core */
import { Engine } from "@babylonjs/core/Engines/engine";
import { Scene } from "@babylonjs/core/scene";
import { Constants } from "@babylonjs/core/Engines/constants";

/* Cameras */
import { FreeCamera } from "@babylonjs/core/Cameras/freeCamera";
import { ArcRotateCamera } from "@babylonjs/core/Cameras/arcRotateCamera";
import { GamepadManager } from "@babylonjs/core/Gamepads/gamepadManager";
import { UniversalCamera } from "@babylonjs/core/Cameras/universalCamera";

/* Maths */
import { Color3, Vector3 } from "@babylonjs/core/Maths/math";

/* Lights */
import { HemisphericLight } from "@babylonjs/core/Lights/hemisphericLight";
import { SpotLight } from "@babylonjs/core/Lights/spotLight";

/* Meshes */
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { Mesh } from "@babylonjs/core/Meshes/mesh";

/* Shadows */
import { ShadowGenerator } from "@babylonjs/core/Lights/Shadows/shadowGenerator";
import { ShadowGeneratorSceneComponent } from "@babylonjs/core/Lights/Shadows/shadowGeneratorSceneComponent";

/* Animation */
import { AnimationPropertiesOverride } from "@babylonjs/core/Animations/animationPropertiesOverride";

/* Materials/Textures */
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Texture } from "@babylonjs/core/Materials/Textures/texture";
import { CubeTexture } from "@babylonjs/core/Materials/Textures/cubeTexture";

/* Controls */
import Gamepad from "../Controls/Inputs/Gamepad";
import TouchJoysticks from "../Controls/Inputs/TouchJoysticks"

/* Libs */
import * as PIXI from 'pixi.js';
import * as screenfull from "screenfull";


import Sprite from "../GameObjects/Sprite";
import Ground from "../GameObjects/Ground";
import Land from "../GameObjects/Land";
import Wall from "../GameObjects/Wall"
import Player from "../Player/Player";
import PlayerSphere from "../GameObjects/PlayerSphere";


export default class Game {

    constructor(canvasElement) {

        this.canvas = canvasElement;

        this.fullscreen = false

        this.engine = createEngine(this.canvas);

        this.pixiRenderer = createPixiRenderer(this.engine);

        this.pixiContainer = createPixiContainer();

        this.scene = createScene(this.engine);

        this.camera = createCamera(this.canvas, this.scene);

        this.skybox = createSkybox(this.scene)

        this.lights = createLights(this.scene);

        this.shadowGenerator = createShadows(this.scene, this.lights);

        this.gameObjects = createGameObjects(this.scene, this.shadowGenerator);

        this.ui = createUI(this.pixiContainer);

        this.controls = createControls();

        // this.player = new Player(this.scene);

        // this.capabilities = checkWebGLCapapabilities(this.canvas);



        function createEngine(canvas) {

            const engine = new Engine(canvas, true);
            engine.enableOfflineSupport = false;
            //engine.setHardwareScalingLevel(1.0);

            return engine;
        }

        function createPixiRenderer(engine) {
            const pixiRenderer = new PIXI.WebGLRenderer({
                context: engine._gl,
                view: engine.getRenderingCanvas(),
                width: engine.getRenderWidth(),
                height: engine.getRenderHeight(),
                clearBeforeRender: false,
                roundPixels: true,
                autoStart: false
            });

            return pixiRenderer
        }


        function createPixiContainer() {

            const pixiContainer = new PIXI.Container();

            return pixiContainer
        }


        function createScene(engine) {

            const scene = new Scene(engine);
            scene.clearColor = new Color3(1, 1, 1);
            scene.ambientColor = new Color3(0.2, 0, 0.9);

            scene.fogMode = Scene.FOGMODE_LINEAR
            scene.fogColor = new Color3(1, 1, 5);
            scene.fogStart = 20.0;
            scene.fogEnd = 800.0;
            scene.fogDensity = 0.1;

            scene.gravity = new Vector3(0, -0.9, 0);
            scene.collisionsEnabled = true;


            // Make Shaders reload automatically
            // scene.markAllMaterialsAsDirty(Constants.MATERIAL_AllDirtyFlag);




            return scene;
        }


        function createSkybox(scene) {

            const skybox = Mesh.CreateBox("skyBox", 100.0, scene);

            const skyboxMaterial = new StandardMaterial("skyBox", scene);
            skyboxMaterial.backFaceCulling = false;
            skyboxMaterial.disableLighting = true;
            skyboxMaterial.reflectionTexture = new CubeTexture("./assets/textures/skybox", scene);
            skyboxMaterial.reflectionTexture.coordinatesMode = Texture.SKYBOX_MODE;

            skybox.material = skyboxMaterial;

            skybox.infiniteDistance = true;

            skybox.renderingGroupId = 0;

            return skybox
        }


        function createLights(scene) {
            // Parameters: name, position, scene
            // const hemLight = new HemisphericLight('hemLight', new Vector3(0.04, 0, 1), scene);
            const spotLight = new SpotLight("spotLight", new Vector3(3, 40, 0), new Vector3(0, -1, 0), 180, 64, scene);



            return spotLight;
        }

        function createShadows(scene, lights) {
            const shadowGenerator = new ShadowGenerator(1024, lights);
            shadowGenerator.setDarkness(0.5);


            return shadowGenerator;
        }


        function createCamera(canvas, scene) {
            const camera = new FreeCamera("FreeCamera", new Vector3(-20, 0, -10), scene);
            // const camera = new ArcRotateCamera("ArcRotateCamera", 1, 0.8, 10, new Vector3(0, 5, 10), scene);

            camera.attachControl(canvas, true);

            camera.setTarget(new Vector3(0, -5, 0))
            camera.minZ = 0;
            camera.maxZ = 100000;

            // Enable camera collisions
            camera.ellipsoid = new Vector3(1, 1, 1);
            camera.checkCollisions = true;
            camera.applyGravity = true;

            return camera;
        }


        function createGameObjects(scene, shadowGenerator) {

            const player = new Player(scene);
            const playerSphere = new PlayerSphere(scene);
            const land = new Land(scene);
            const ground = new Ground(scene);
            const wall = new Wall(scene);


            shadowGenerator.getShadowMap().renderList.push(wall.mesh);
            // shadowGenerator.getShadowMap().renderList.push(playerSphere.mesh)

            return scene.meshes;
        }

        function createUI(pixiContainer) {
            // const sprite = new PIXI.Sprite.from('assets/sprites/gamepad.png');
            // pixiContainer.addChild(sprite);
        }

        function createControls() {

            const gamepad = new Gamepad();
            gamepad.listenForGamepads

            return gamepad;

        }

        function createTouchJoysticks() {

            const touchJoysticks = new TouchJoysticks()

            return touchJoysticks;
        }

        this.render = () => {

            this.engine.runRenderLoop(() => {
                this.scene.render();
                this.engine.wipeCaches(true);
                this.pixiRenderer.reset();
                this.pixiRenderer.render(this.pixiContainer);
            });

            return this
        }

        this.onToggleFullscreen = () =>

            window.addEventListener("keydown", (event) => {

                if (event.code === "KeyF" && screenfull.enabled) {

                    if (this.fullscreen === false) {
                        screenfull.request();
                        this.fullscreen = true;

                    } else {
                        screenfull.exit(this.canvas);
                        this.fullscreen = false;
                    }
                }
            });

        this.onWindowResize = () => {

            this.engine.resize();

        }
    }
}